import { useMemo, useState } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useActivities, useTrails, useWeather, useTides } from '../hooks/data.js';
import { topWindowsForActivity } from '../lib/scoring.js';
import { isGoldenHour, sunPosition, nextGoldenHour, goldenHoursFor } from '../lib/sun.js';
import ConditionScore from '../components/ConditionScore.jsx';
import { formatDateShort, formatTime } from '../lib/format.js';

// Open-Meteo weather code → minimal text label. Codes per WMO; we
// collapse the granular variants ("light/moderate/heavy rain") into
// the broader bucket because the strip is a 36-px-wide cell.
function weatherCodeLabel(code) {
  if (code == null) return '—';
  if (code === 0) return 'clear';
  if (code === 1) return 'mostly clear';
  if (code === 2) return 'partly cloudy';
  if (code === 3) return 'overcast';
  if (code === 45 || code === 48) return 'fog';
  if (code >= 51 && code <= 57) return 'drizzle';
  if (code >= 61 && code <= 67) return 'rain';
  if (code >= 71 && code <= 77) return 'snow';
  if (code >= 80 && code <= 82) return 'showers';
  if (code >= 85 && code <= 86) return 'snow showers';
  if (code >= 95 && code <= 99) return 'thunder';
  return '—';
}

// Glyph for a weather code — kept to a small set of monochrome
// symbols so it reads on dark forest theme without color clash.
function weatherCodeGlyph(code) {
  if (code == null) return '·';
  if (code === 0) return '☼';
  if (code === 1 || code === 2) return '⛅';
  if (code === 3 || (code >= 45 && code <= 48)) return '☁';
  if (code >= 51 && code <= 67) return '☂';
  if (code >= 71 && code <= 86) return '❄';
  if (code >= 80 && code <= 82) return '☂';
  if (code >= 95) return '⚡';
  return '·';
}

// Pre-trip the photo-opportunity recommendation cards underperform —
// the GPS is in Portland, the candidate trails are all in Juneau,
// and they collapse to identical ~6.2 scores in the same 7–8 PM window.
// Hidden until trip-start lands the player inside the Juneau bounding
// box. Flip to `true` (or wire a GPS-based gate) post-trip to re-enable.
const SHOW_PHOTO_OPPORTUNITIES = false;

export default function Today() {
  const { tripId, trip } = useTrip();
  const lat = trip?.destination?.lat ?? 58.3019;
  const lng = trip?.destination?.lng ?? -134.4197;
  const tz = trip?.dates?.timezone ?? 'America/Juneau';

  const activities = useActivities(tripId);
  const trails = useTrails();
  const weather = useWeather({ lat, lng, hours: 72, timezone: tz });
  const tides = useTides({ hours: 72 });

  const recs = useMemo(() => {
    if (!activities.data?.activities || !weather.data?.hourly) return [];
    const trailMap = trails.data?.trails || {};
    const tideSeries = tides.data?.series || [];

    const all = [];
    for (const a of activities.data.activities) {
      const wins = topWindowsForActivity({
        activity: a,
        weather: weather.data.hourly,
        tideSeries,
        originLat: lat,
        originLng: lng,
        topN: 2,
        windowHours: 2,
      });
      for (const w of wins) {
        all.push({ activity: a, trail: a.trail_id ? trailMap[a.trail_id] : null, ...w });
      }
    }
    all.sort((a, b) => b.peak.score - a.peak.score);
    // Spread across activities — don't return three windows on the same trail.
    const seen = new Set();
    const top = [];
    for (const item of all) {
      if (seen.has(item.activity.id)) continue;
      seen.add(item.activity.id);
      top.push(item);
      if (top.length >= 3) break;
    }
    return top;
  }, [activities.data, trails.data, weather.data, tides.data, lat, lng]);

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-4 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          live conditions · 72h horizon
        </p>
        <h1 className="mt-1 font-serif text-2xl">Today</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          Conditions now, hourly forecast, and the three-day shape of the weather. Photo-opportunity
          windows return on day one in Juneau.
        </p>
      </header>

      <NowPanel
        weather={weather}
        tides={tides}
        lat={lat}
        lng={lng}
        timezone={tz}
      />

      <HourlyStrip
        weather={weather}
        lat={lat}
        lng={lng}
        timezone={tz}
      />

      <ThreeDayMini weather={weather} timezone={tz} />

      {weather.error && (
        <div className="card px-4 py-3 text-xs text-danger">
          Weather feed unavailable — {weather.error.message}
        </div>
      )}

      {SHOW_PHOTO_OPPORTUNITIES && (activities.loading || weather.loading) && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          …computing windows
        </p>
      )}

      {SHOW_PHOTO_OPPORTUNITIES && (
        <ol className="space-y-3">
          {recs.map((r, i) => (
            <Recommendation
              key={`${r.activity.id}-${r.start.toISOString()}`}
              rec={r}
              rank={i + 1}
              timezone={tz}
            />
          ))}
        </ol>
      )}

      {SHOW_PHOTO_OPPORTUNITIES &&
        !activities.loading &&
        !weather.loading &&
        recs.length === 0 && (
          <div className="card px-4 py-6 text-center text-sm text-fg-secondary">
            No clear windows in the next 72h yet. Pull the trail or tour list and pick by score.
          </div>
        )}
    </div>
  );
}

function NowPanel({ weather, tides, lat, lng, timezone }) {
  const now = useMemo(() => new Date(), []);
  const hour = useMemo(() => currentHour(weather.data, now), [weather.data, now]);
  const tideNow = useMemo(() => currentTide(tides.data, now), [tides.data, now]);
  const sun = useMemo(() => sunPosition(now, lat, lng), [lat, lng, now]);
  const golden = useMemo(() => isGoldenHour(now, lat, lng), [now, lat, lng]);
  const nextGolden = useMemo(() => nextGoldenHour(now, lat, lng), [now, lat, lng]);
  const today = useMemo(() => goldenHoursFor(now, lat, lng), [now, lat, lng]);
  const sunsetTimes = useMemo(() => sunSetRiseTimes(weather.data, now), [weather.data, now]);
  const upcomingTides = useMemo(() => upcomingExtremes(tides.data, now, 3), [tides.data, now]);

  const ts = now.toLocaleTimeString('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  if (!hour && !tideNow) {
    return weather.loading ? null : null;
  }

  return (
    <section
      aria-labelledby="now-panel"
      className="card overflow-hidden"
    >
      <header className="flex items-baseline justify-between gap-2 border-b border-border-tertiary bg-bg-secondary px-4 py-2">
        <h2
          id="now-panel"
          className="font-mono text-[10px] uppercase tracking-wider text-fg-secondary"
        >
          conditions now
        </h2>
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">{ts}</p>
      </header>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 px-4 py-3 text-sm">
        {hour && (
          <Cell label="weather" primary={`${formatTemp(hour.temperature_2m)} · ${formatPct(hour.cloud_cover)} cloud`}>
            <span className="text-fg-tertiary">
              {formatPct(hour.precipitation_probability)} precip · wind {Math.round(hour.wind_speed_10m || 0)} mph
              {hour.wind_gusts_10m ? ` · gust ${Math.round(hour.wind_gusts_10m)}` : ''}
            </span>
          </Cell>
        )}
        {hour && (
          <Cell
            label="ceiling"
            primary={hour.cloud_base != null ? `${Math.round(hour.cloud_base)} ft` : '—'}
          >
            <span className="text-fg-tertiary">
              visibility{' '}
              {hour.visibility != null
                ? `${(Math.round(hour.visibility / 100) / 10).toFixed(1)} km`
                : '—'}
            </span>
          </Cell>
        )}
        <Cell
          label="sun"
          primary={`${sun.altitudeDeg.toFixed(1)}° · ${bearingLabel(sun.azimuthDeg)}`}
        >
          <span className="text-fg-tertiary">
            {golden
              ? `golden hour now (${golden === 'morning_golden' ? 'morning' : 'evening'})`
              : nextGolden
                ? `next golden ${formatRelative(nextGolden.start, timezone, now)} · ${nextGolden.label === 'morning_golden' ? 'morning' : 'evening'}`
                : 'no golden hour today'}
          </span>
        </Cell>
        <Cell
          label="tide"
          primary={
            tideNow?.v != null
              ? `${Number(tideNow.v).toFixed(1)} ft`
              : '—'
          }
        >
          <span className="text-fg-tertiary">
            {upcomingTides.length > 0
              ? upcomingTides
                  .map(
                    (t) =>
                      `${t.type} ${formatRelative(new Date(t.t), timezone, now)} (${Number(t.v).toFixed(1)}ft)`
                  )
                  .join(' · ')
              : 'no extremes in horizon'}
          </span>
        </Cell>
      </dl>
      {(today.morning || today.evening || sunsetTimes) && (
        <p className="border-t border-border-tertiary bg-bg-secondary px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          {sunsetTimes ? (
            <>
              sunrise{' '}
              <span className="normal-case tracking-normal text-fg-secondary">
                {sunsetTimes.sunrise
                  ? formatTime(sunsetTimes.sunrise, { timezone, withZone: false })
                  : '—'}
              </span>
              {' · sunset '}
              <span className="normal-case tracking-normal text-fg-secondary">
                {sunsetTimes.sunset
                  ? formatTime(sunsetTimes.sunset, { timezone, withZone: false })
                  : '—'}
              </span>
            </>
          ) : null}
          {today.evening ? (
            <>
              {' · evening golden '}
              <span className="normal-case tracking-normal text-fg-secondary">
                {formatTime(today.evening.start.toISOString(), { timezone, withZone: false })}–
                {formatTime(today.evening.end.toISOString(), { timezone, withZone: false })}
              </span>
            </>
          ) : null}
        </p>
      )}
    </section>
  );
}

// 12–24 hour horizontal strip. Each cell shows time, glyph, temp,
// precip %, wind. Cells inside a golden-hour window get a copper
// border. Tapping a cell expands a detail row below the strip.
function HourlyStrip({ weather, lat, lng, timezone }) {
  const now = useMemo(() => new Date(), []);
  const hours = useMemo(() => sliceHourly(weather.data, now, 18), [weather.data, now]);
  const goldenWindows = useMemo(() => goldenWindowsAhead(now, lat, lng, 36), [now, lat, lng]);
  const [openIndex, setOpenIndex] = useState(null);

  if (!hours || hours.length === 0) return null;
  const opened = openIndex != null ? hours[openIndex] : null;

  return (
    <section
      aria-labelledby="hourly-strip"
      className="card overflow-hidden"
    >
      <header className="flex items-baseline justify-between gap-2 border-b border-border-tertiary bg-bg-secondary px-4 py-2">
        <h2
          id="hourly-strip"
          className="font-mono text-[10px] uppercase tracking-wider text-fg-secondary"
        >
          next {hours.length} hours
        </h2>
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          tap a cell for detail
        </p>
      </header>

      <div className="overflow-x-auto">
        <ul className="flex gap-1 px-3 py-3">
          {hours.map((h, i) => {
            const isGolden = inAnyWindow(h.timeMs, goldenWindows);
            const isSelected = openIndex === i;
            return (
              <li key={h.timeMs} className="shrink-0">
                <button
                  type="button"
                  onClick={() => setOpenIndex(isSelected ? null : i)}
                  className={[
                    'flex w-16 flex-col items-center gap-0.5 rounded-md border px-1 py-2 transition',
                    isSelected
                      ? 'border-action bg-action/15'
                      : isGolden
                        ? 'border-copper/50 bg-copper/10 hover:bg-copper/15'
                        : 'border-border-tertiary bg-bg-secondary hover:bg-bg-tertiary',
                  ].join(' ')}
                >
                  <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                    {formatHourLabel(h.timeMs, timezone)}
                  </span>
                  <span aria-hidden className="text-base text-fg-primary">
                    {weatherCodeGlyph(h.weather_code)}
                  </span>
                  <span className="font-mono text-xs text-fg-primary">
                    {Math.round(h.temperature_2m ?? 0)}°
                  </span>
                  <span className="font-mono text-[10px] text-fg-tertiary">
                    {Math.round(h.precipitation_probability ?? 0)}%
                  </span>
                  <span className="font-mono text-[10px] text-fg-tertiary">
                    {Math.round(h.wind_speed_10m ?? 0)} mph
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {opened && (
        <div className="space-y-1 border-t border-border-tertiary bg-bg-secondary px-4 py-3 text-xs text-fg-secondary">
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            {formatHourLong(opened.timeMs, timezone)} ·{' '}
            <span className="normal-case tracking-normal text-fg-secondary">
              {weatherCodeLabel(opened.weather_code)}
            </span>
          </p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
            <DetailRow label="temp" value={`${Math.round(opened.temperature_2m ?? 0)}°F`} />
            <DetailRow label="apparent" value={`${Math.round(opened.apparent_temperature ?? 0)}°F`} />
            <DetailRow label="precip" value={`${Math.round(opened.precipitation_probability ?? 0)}% · ${(opened.precipitation ?? 0).toFixed(2)} in`} />
            <DetailRow label="cloud" value={`${Math.round(opened.cloud_cover ?? 0)}%`} />
            <DetailRow
              label="ceiling"
              value={opened.cloud_base != null ? `${Math.round(opened.cloud_base)} ft` : '—'}
            />
            <DetailRow
              label="visibility"
              value={
                opened.visibility != null
                  ? `${(Math.round(opened.visibility / 100) / 10).toFixed(1)} km`
                  : '—'
              }
            />
            <DetailRow
              label="wind"
              value={`${Math.round(opened.wind_speed_10m ?? 0)} mph${
                opened.wind_gusts_10m ? ` · gust ${Math.round(opened.wind_gusts_10m)}` : ''
              }`}
            />
            <DetailRow
              label="dir"
              value={
                opened.wind_direction_10m != null
                  ? bearingLabel(opened.wind_direction_10m)
                  : '—'
              }
            />
          </div>
        </div>
      )}
    </section>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-1">
      <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        {label}
      </span>
      <span className="font-medium text-fg-primary">{value}</span>
    </div>
  );
}

// 3-day mini-forecast — daily aggregates from open-meteo.
function ThreeDayMini({ weather, timezone }) {
  const now = useMemo(() => new Date(), []);
  const days = useMemo(() => sliceDaily(weather.data, now, 3), [weather.data, now]);
  if (!days || days.length === 0) return null;

  return (
    <section
      aria-labelledby="three-day"
      className="card overflow-hidden"
    >
      <header className="border-b border-border-tertiary bg-bg-secondary px-4 py-2">
        <h2
          id="three-day"
          className="font-mono text-[10px] uppercase tracking-wider text-fg-secondary"
        >
          next 3 days
        </h2>
      </header>
      <ul className="divide-y divide-border-tertiary">
        {days.map((d) => (
          <li key={d.dateIso} className="flex items-baseline justify-between gap-3 px-4 py-2">
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                {formatDayLabel(d.dateIso, timezone)}
              </p>
              <p className="text-sm text-fg-primary">
                <span aria-hidden className="mr-1.5 text-base">
                  {weatherCodeGlyph(d.weather_code)}
                </span>
                {weatherCodeLabel(d.weather_code)}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <p className="font-mono text-sm text-fg-primary">
                {Math.round(d.temperature_2m_max ?? 0)}° / {Math.round(d.temperature_2m_min ?? 0)}°
              </p>
              <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                {Math.round(d.precipitation_probability_max ?? 0)}% precip
                {d.wind_speed_10m_max != null
                  ? ` · wind ${Math.round(d.wind_speed_10m_max)}`
                  : ''}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---- helpers used by HourlyStrip + ThreeDayMini --------------------

function sliceHourly(weatherData, now, hoursAhead) {
  if (!weatherData?.hourly?.time) return [];
  const times = weatherData.hourly.time;
  const nowMs = now.getTime();
  const out = [];
  for (let i = 0; i < times.length && out.length < hoursAhead; i += 1) {
    const tMs = Date.parse(times[i]);
    if (Number.isNaN(tMs)) continue;
    if (tMs < nowMs - 30 * 60_000) continue; // skip past hours, tolerate 30 min drift
    const hr = {};
    for (const k of Object.keys(weatherData.hourly)) {
      hr[k] = Array.isArray(weatherData.hourly[k]) ? weatherData.hourly[k][i] : null;
    }
    hr.timeMs = tMs;
    out.push(hr);
  }
  return out;
}

function sliceDaily(weatherData, now, daysAhead) {
  if (!weatherData?.daily?.time) return [];
  const times = weatherData.daily.time;
  const todayKey = isoDateKey(now);
  const out = [];
  for (let i = 0; i < times.length && out.length < daysAhead; i += 1) {
    if (times[i] < todayKey) continue;
    const d = { dateIso: times[i] };
    for (const k of Object.keys(weatherData.daily)) {
      d[k] = Array.isArray(weatherData.daily[k]) ? weatherData.daily[k][i] : null;
    }
    out.push(d);
  }
  return out;
}

function isoDateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function goldenWindowsAhead(now, lat, lng, hoursAhead) {
  const out = [];
  for (let dayOffset = 0; dayOffset <= Math.ceil(hoursAhead / 24); dayOffset += 1) {
    const day = new Date(now.getTime() + dayOffset * 24 * 60 * 60_000);
    const { morning, evening } = goldenHoursFor(day, lat, lng);
    if (morning) out.push({ start: morning.start.getTime(), end: morning.end.getTime() });
    if (evening) out.push({ start: evening.start.getTime(), end: evening.end.getTime() });
  }
  return out;
}

function inAnyWindow(ms, windows) {
  for (const w of windows) {
    if (ms >= w.start && ms <= w.end) return true;
  }
  return false;
}

function formatHourLabel(ms, tz) {
  return new Date(ms).toLocaleTimeString('en-US', {
    timeZone: tz,
    hour: 'numeric',
    hour12: true,
  }).replace(/\s/g, '').toLowerCase();
}

function formatHourLong(ms, tz) {
  return new Date(ms).toLocaleString('en-US', {
    timeZone: tz,
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatDayLabel(dateIso, tz) {
  return new Date(`${dateIso}T12:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: tz,
  });
}

function Cell({ label, primary, children }) {
  return (
    <div>
      <p className="font-mono text-[9px] uppercase tracking-wider text-fg-tertiary">{label}</p>
      <p className="font-medium text-fg-primary leading-tight">{primary}</p>
      <p className="font-mono text-[10px] leading-tight">{children}</p>
    </div>
  );
}

function currentHour(weatherData, now) {
  if (!weatherData?.hourly?.time?.length) return null;
  const ms = now.getTime();
  let bestIdx = 0;
  let bestDiff = Infinity;
  for (let i = 0; i < weatherData.hourly.time.length; i += 1) {
    const t = Date.parse(weatherData.hourly.time[i]);
    if (Number.isNaN(t)) continue;
    const diff = Math.abs(ms - t);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }
  const out = {};
  for (const k of Object.keys(weatherData.hourly)) {
    if (Array.isArray(weatherData.hourly[k])) out[k] = weatherData.hourly[k][bestIdx];
  }
  return out;
}

function currentTide(tideData, now) {
  if (!tideData?.series?.length) return null;
  const ms = now.getTime();
  let best = tideData.series[0];
  let bestDiff = Infinity;
  for (const row of tideData.series) {
    const diff = Math.abs(ms - new Date(row.t).getTime());
    if (diff < bestDiff) {
      bestDiff = diff;
      best = row;
    }
  }
  return best;
}

function upcomingExtremes(tideData, now, limit) {
  if (!tideData?.extremes?.length) return [];
  const ms = now.getTime();
  return tideData.extremes
    .filter((e) => new Date(e.t).getTime() > ms)
    .slice(0, limit)
    .map((e) => ({ ...e, type: e.type === 'H' ? 'high' : 'low' }));
}

function sunSetRiseTimes(weatherData, now) {
  if (!weatherData?.daily?.time?.length) return null;
  const today = now.toISOString().slice(0, 10);
  const idx = weatherData.daily.time.findIndex((d) => d === today);
  if (idx < 0) return null;
  return {
    sunrise: weatherData.daily.sunrise?.[idx] || null,
    sunset: weatherData.daily.sunset?.[idx] || null,
  };
}

function bearingLabel(deg) {
  // SunCalc azimuth: 0 = south, +π/2 = west. Normalise to 0–360 from north.
  let a = (deg + 180) % 360;
  if (a < 0) a += 360;
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return dirs[Math.round(a / 45) % 8];
}

function formatTemp(v) {
  if (v == null) return '—';
  return `${Math.round(v)}°F`;
}
function formatPct(v) {
  if (v == null) return '—';
  return `${Math.round(v)}%`;
}
function formatRelative(when, timezone, now) {
  const ms = (when instanceof Date ? when : new Date(when)).getTime() - now.getTime();
  if (Number.isNaN(ms)) return '—';
  if (ms < 0) {
    const past = -ms;
    const min = Math.round(past / 60000);
    if (min < 60) return `${min}m ago`;
    return formatTime((when instanceof Date ? when : new Date(when)).toISOString(), { timezone, withZone: false });
  }
  const min = Math.round(ms / 60000);
  if (min < 90) return `in ${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 36) {
    const at = formatTime((when instanceof Date ? when : new Date(when)).toISOString(), { timezone, withZone: false });
    return `${at} (in ${hr}h)`;
  }
  return formatTime((when instanceof Date ? when : new Date(when)).toISOString(), { timezone, withZone: false });
}

function Recommendation({ rec, rank, timezone }) {
  const { activity, trail, peak, start, end, avgScore } = rec;
  const name = activity.name || trail?.name || activity.id;
  const photo = trail?.profile_photo_url;
  const golden = isGoldenHour(peak.when, activity.lat ?? trail?.trailhead?.lat ?? 58.3, activity.lng ?? trail?.trailhead?.lng ?? -134.4);
  const reason = peak.reasons?.[0] || (golden ? 'Golden-hour window' : 'Conditions align');

  return (
    <li className="card overflow-hidden">
      <div className="flex items-start gap-3 p-3">
        {photo ? (
          <img
            src={photo}
            alt=""
            className="h-16 w-16 shrink-0 rounded-md object-cover"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md bg-bg-tertiary font-mono text-[10px] uppercase text-fg-tertiary">
            {activity.type}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              window {rank} · {formatDateShort(start.toISOString(), { timezone })}
            </p>
            <ConditionScore score={peak.score} size="sm" label="" />
          </div>
          <h3 className="truncate font-medium">{name}</h3>
          <p className="font-mono text-xs text-fg-secondary">
            {formatTime(start.toISOString(), { timezone, withZone: false })} →{' '}
            {formatTime(end.toISOString(), { timezone })}
            {avgScore != null && (
              <span className="ml-2 text-fg-tertiary">avg {avgScore.toFixed(1)}</span>
            )}
          </p>
          <p className="mt-1 text-xs text-fg-secondary">{reason}</p>
        </div>
      </div>
    </li>
  );
}
